import { useEnvironmentVariable } from "@squide/firefly";
import { useCallback, useState } from "react";
import { triggerDownload } from "./triggerDownload.ts";

function resolveFileName(disposition: string | null, fallback: string) {
    const encoded = disposition?.match(/filename\*=UTF-8''([^;]+)/i)?.[1];

    if (encoded) {
        return decodeURIComponent(encoded);
    }

    return disposition?.match(/filename="([^"]+)"/)?.[1] ?? fallback;
}

export interface FileExportRequest {
    path: string;
    input: object;
    fallbackFileName: string;
}

export interface UseFileExportResult {
    exportFile: (request: FileExportRequest) => Promise<boolean>;
    isExporting: boolean;
}

export function useFileExport(language: string): UseFileExportResult {
    const apiBaseUrl = useEnvironmentVariable("apiBaseUrl");
    const [isExporting, setIsExporting] = useState(false);

    const exportFile = useCallback(
        async ({ path, input, fallbackFileName }: FileExportRequest) => {
            setIsExporting(true);

            try {
                const response = await fetch(
                    new Request(`${apiBaseUrl}${path}`, {
                        method: "POST",
                        credentials: "include",
                        headers: { "Content-Type": "application/json", "Accept-Language": language },
                        body: JSON.stringify(input)
                    })
                );

                if (!response.ok) {
                    return false;
                }

                triggerDownload(await response.blob(), resolveFileName(response.headers.get("Content-Disposition"), fallbackFileName));

                return true;
            } catch {
                return false;
            } finally {
                setIsExporting(false);
            }
        },
        [apiBaseUrl, language]
    );

    return { exportFile, isExporting };
}
